import type { ApprovalForm } from '@/pages/approval/useApproval';

/**
 * 서식 고르기 — <b>업무 갈래로 묶어</b> 보여 준다.
 *
 * 예전에는 한 줄에 `재직증명서 발급 · 인사` 처럼 서식명과 갈래를 붙여 놓고 스무 개를 한 목록에 쏟았다.
 * 고르는 사람은 대개 <b>갈래를 먼저 안다</b> — "인사 쪽 뭔가를 신청하려는데" 로 시작해서 그 안에서 고른다.
 * 그런데 갈래가 이름 뒤에 붙어 있으니 눈이 줄마다 끝까지 갔다 와야 했고, 같은 갈래가 목록 여기저기에
 * 흩어져 있었다(정렬이 서식 등록순이라서). 이제 갈래가 머리글({@code optgroup})이고 이름만 남는다.
 *
 * 순서는 자주 쓰는 갈래부터 손으로 정한다. 이름순으로 두면 「자산」 이 「인사」 보다 앞에 서는데,
 * 자산 품의는 일 년에 몇 번이고 연차·증명서는 매주 올라간다.
 */
const CATEGORY_ORDER = ['인사', '총무', '구매', '회계', '자산', '일반'];

/** 갈래가 비어 있는 서식이 목록에서 사라지지 않게 받아 두는 자리. */
const FALLBACK = '기타';

/**
 * 기안 목록에서 <b>감추는</b> 서식 (사용자 지시 2026-08-26).
 *
 * <p>{@code LEAVE_PLAN}(휴무 계획서)은 이제 결재가 아니다 — 「휴무 관리」 와 마이페이지의 달력에서
 * 날짜를 골라 바로 저장한다. 계획은 승인 대상이 아니라 시기를 알리는 값이라, 결재선을 짜게 두면
 * 「쉬겠다고 적는 일」 이 결재 한 건이 되고 그래서 아무도 적지 않았다.
 *
 * <p><b>서식 자체를 지우지는 않는다.</b> 예전에 그 서식으로 올라간 문서가 결재함에 남아 있고,
 * 서식이 사라지면 그 문서들이 이름 없는 기안으로 열린다. 새로 <b>고를 수만</b> 없게 한다.
 *
 * <p>이 목록을 기안·위임·전결규정 세 화면이 함께 쓴다. 셋 다 새로 고를 일이 없어졌으므로 한 곳에서
 * 거른다 — 결재를 받지 않는 서식에 전결규정이나 위임을 걸어 두면 아무 일도 일어나지 않는다.
 */
const HIDDEN_FORM_KEYS = new Set(['LEAVE_PLAN']);

/**
 * 새로 <b>기안할 수 있는</b> 서식인가 (사용자 지시 2026-10-06 · 2026-10-14).
 *
 * <p>처음에는 여기 코드 상수(ENABLED_FORM_KEYS)로 여덟 개를 열어 두었다. 그러면 하나 열 때마다
 * 코드를 고쳐 배포해야 해서 「웹에서 오픈할 수 있도록 조작이 안 된다」 — 이제 서버가 오픈
 * 게이트({@code sys_menu_gate} · V467)를 보고 서식 응답에 {@code draftOpen} 으로 실어 준다.
 * 켜고 끄는 자리는 결재 화면 서식 카드 옆의 스위치({@code FormGateSwitch} · 최고관리자).
 *
 * <p>닫힌 서식은 감추지 않고 회색으로 서서 눌리지 않는다. 이미 올라간 문서·임시저장을 여는 길은
 * 막지 않는다 — 새 기안을 고르는 자리에만 건다. 실제 차단은 서버가 한다(새 기안 만들기 API).
 *
 * <p>칸이 아직 없으면(옛 서버) 닫힌 것으로 본다 — 열어서는 안 될 서식이 잠깐이라도 열리는 쪽보다
 * 회색으로 서 있는 쪽이 안전하다.
 */
export function isFormEnabled(form: Pick<ApprovalForm, 'draftOpen'>): boolean {
  return form.draftOpen === true;
}

/** 오픈 게이트에서 이 서식을 가리키는 키 — 백엔드 {@code ApprovalForm.gateKey} 와 같은 글자. */
export function formGateKey(formKey: string): string {
  return `approval_form_${formKey}`;
}

export type FormGroup = { category: string; forms: ApprovalForm[] };

/** 맨 앞 묶음 — 내가 최근에 쓴 서식 (사용자 지시 2026-10-06). 갈래가 아니라 «나» 로 묶는다. */
export const RECENT_CATEGORY = '최근 사용';
const RECENT_LIMIT = 5;

/**
 * 갈래별로 묶는다. 묶음 안의 순서는 서버가 준 그대로다({@code sort_order} → 이름) —
 * 여기서 다시 정렬하면 관리자가 서식 순서를 고쳐도 화면이 듣지 않는다.
 *
 * <p>새 기안 자리({@code forDraft})에서는 두 가지가 더해진다 (사용자 지시 2026-10-06):
 * 묶음 안에서 <b>누를 수 있는 서식이 앞</b>에 서고(그 안의 순서는 서버 순서 그대로),
 * {@code recentKeys}(최근 쓴 순)에서 누를 수 있는 것 다섯이 「최근 사용」 묶음으로 맨 앞에 선다.
 * 같은 서식이 「최근 사용」 과 제 갈래에 함께 선다 — 찾는 길이 둘일 뿐 다른 서식이 아니다.
 * 전결규정·위임은 새 기안이 아니라 이 둘을 받지 않는다.
 */
export function groupForms(
  forms: ApprovalForm[],
  { forDraft = false, recentKeys = [] }: { forDraft?: boolean; recentKeys?: readonly string[] } = {},
): FormGroup[] {
  const groups = new Map<string, ApprovalForm[]>();
  for (const form of forms) {
    if (HIDDEN_FORM_KEYS.has(form.formKey)) continue;
    const category = form.category?.trim() || FALLBACK;
    const bucket = groups.get(category);
    if (bucket) bucket.push(form);
    else groups.set(category, [form]);
  }

  const rank = (category: string) => {
    const at = CATEGORY_ORDER.indexOf(category);
    return at === -1 ? CATEGORY_ORDER.length : at;
  };
  const sorted = [...groups.entries()]
    .map(([category, list]) => ({
      category,
      forms: forDraft
        ? [...list.filter((f) => isFormEnabled(f)), ...list.filter((f) => !isFormEnabled(f))]
        : list,
    }))
    .sort((a, b) => rank(a.category) - rank(b.category) || a.category.localeCompare(b.category, 'ko'));
  if (!forDraft) return sorted;

  const byKey = new Map(forms.map((form) => [form.formKey, form]));
  const recent = recentKeys
    .filter((key) => !HIDDEN_FORM_KEYS.has(key))
    .map((key) => byKey.get(key))
    .filter((form): form is ApprovalForm => form !== undefined && isFormEnabled(form))
    .slice(0, RECENT_LIMIT);
  // 쓴 것이 없으면 묶음도 없다 — 빈 제목만 서 있으면 한 줄을 이유 없이 먹는다.
  return recent.length > 0 ? [{ category: RECENT_CATEGORY, forms: recent }, ...sorted] : sorted;
}

/**
 * `<select>` 안에 그대로 넣는 갈래별 묶음. 기안·전결규정·위임이 서식 목록을 각자 그리던 때에는
 * 같은 서식이 화면마다 다른 순서로 섰다.
 */
export function FormOptions({
  forms,
  enabledOnly = false,
  keepEnabled,
  recentKeys,
}: {
  forms: ApprovalForm[];
  /** 참이면 닫힌 서식({@link isFormEnabled})을 고를 수 없게(disabled) 세운다 — 새 기안 자리 전용. */
  enabledOnly?: boolean;
  /** enabledOnly 여도 살려 둘 서식(이미 열어 둔 문서의 서식). */
  keepEnabled?: string;
  /** 새 기안 자리의 「최근 사용」 묶음 — {@link groupForms} 와 같다. */
  recentKeys?: readonly string[];
}) {
  return (
    <>
      {groupForms(forms, { forDraft: enabledOnly, recentKeys }).map((group) => (
        <optgroup key={group.category} label={group.category}>
          {group.forms.map((form) => (
            <option
              key={form.formKey}
              value={form.formKey}
              disabled={enabledOnly && !isFormEnabled(form) && form.formKey !== keepEnabled}
            >
              {form.name}
            </option>
          ))}
        </optgroup>
      ))}
    </>
  );
}
